import { useCallback, useEffect, useRef, useState } from 'react';
import Image from '@tiptap/extension-image';
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';
import { Fragment, Slice } from '@tiptap/pm/model';
import { NodeSelection, Plugin, PluginKey } from '@tiptap/pm/state';
import { dropPoint } from '@tiptap/pm/transform';
import {
  FIGURE_SPLIT_ALIGN, FIGURE_WIDTH_PRESETS, FIGURE_WRAP_DEFAULT_WIDTH,
  figureAlign, figureClass, figureWidth, isSplitAlign, type FigureAlign,
} from '@blogaris/shared';
import { cx, IconTrash, IconPencil } from '../../components/ui/index.js';

/* ------------------------------------------------------------------ *
 * ไอคอนของแถบจัดวางรูป
 * วาดเป็นภาพจำลอง "รูป + บรรทัดข้อความ" แทนไอคอนลูกศรทั่วไป
 * เพราะผู้ใช้ต้องเดาให้ออกในครั้งเดียวว่าโหมดไหนข้อความจะไหลไปอยู่ตรงไหน
 * ------------------------------------------------------------------ */
const BOX = 'currentColor';
const LINE = 'currentColor';

function LayoutIcon({ mode }: { mode: FigureAlign }) {
  const line = (x: number, y: number, w: number) => (
    <rect key={`${x}-${y}`} x={x} y={y} width={w} height="1.6" rx=".8" fill={LINE} opacity=".38" />
  );
  const box = (x: number, y: number, w: number, h: number) => (
    <rect x={x} y={y} width={w} height={h} rx="1.5" fill={BOX} opacity=".9" />
  );
  return (
    <svg width="17" height="17" viewBox="0 0 18 18" aria-hidden="true">
      {mode === 'center' && <>
        {line(1, 1.5, 16)}{box(3.5, 6, 11, 6)}{line(1, 14.9, 16)}
      </>}
      {mode === 'left' && <>
        {line(1, 1.5, 16)}{box(1, 6, 11, 6)}{line(1, 14.9, 16)}
      </>}
      {mode === 'right' && <>
        {line(1, 1.5, 16)}{box(6, 6, 11, 6)}{line(1, 14.9, 16)}
      </>}
      {mode === 'wrap-left' && <>
        {box(1, 3, 8, 8)}{line(10.5, 3.6, 6.5)}{line(10.5, 6.6, 6.5)}{line(10.5, 9.6, 6.5)}
        {line(1, 13.4, 16)}
      </>}
      {mode === 'wrap-right' && <>
        {box(9, 3, 8, 8)}{line(1, 3.6, 6.5)}{line(1, 6.6, 6.5)}{line(1, 9.6, 6.5)}
        {line(1, 13.4, 16)}
      </>}
      {mode === 'wrap-both' && <>
        {box(6, 3, 6, 8)}
        {line(1, 3.6, 4)}{line(1, 6.6, 4)}{line(1, 9.6, 4)}{line(1, 12.6, 4)}
        {line(13, 3.6, 4)}{line(13, 6.6, 4)}{line(13, 9.6, 4)}{line(13, 12.6, 4)}
      </>}
    </svg>
  );
}

/**
 * ตำแหน่งของรูปที่กำลังถูกลาก
 *
 * ProseMirror จำไว้เองว่ากำลังลาก node ไหน แต่ล้างทิ้งหลัง dragend 50 มิลลิวินาที
 * บนเครื่องจริงเบราว์เซอร์ยิง dragend "ก่อน" drop ได้ พอถึงตอนวางมันจึงไม่เหลือข้อมูลต้นทาง
 * แล้วตีความว่าเป็นการวางของจากที่อื่น = แปะใหม่โดยไม่ลบของเดิม (ลากแล้วกลายเป็นคัดลอก)
 *
 * จึงจำตำแหน่งต้นทางเอง แล้วย้ายเองในทรานแซกชันเดียว ไม่ขึ้นกับลำดับอีเวนต์
 * สำคัญ: ห้ามล้างค่านี้ตอน dragend เด็ดขาด ไม่งั้นจะพังด้วยเหตุผลเดียวกับที่กำลังแก้อยู่
 */
let draggingFigureFrom: number | null = null;

const LAYOUTS: { mode: FigureAlign; label: string }[] = [
  { mode: 'wrap-left',  label: 'ลอยซ้าย — ข้อความไหลอยู่ทางขวา' },
  { mode: 'left',       label: 'ชิดซ้าย' },
  { mode: 'center',     label: 'กึ่งกลาง' },
  { mode: 'right',      label: 'ชิดขวา' },
  { mode: 'wrap-right', label: 'ลอยขวา — ข้อความไหลอยู่ทางซ้าย' },
  { mode: 'wrap-both',  label: 'รูปกลาง ข้อความสองข้าง (แบบนิตยสาร)' },
];

function BarButton({
  active, title, onClick, children,
}: { active?: boolean; title: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button" title={title} aria-label={title} aria-pressed={active} data-figure-ui=""
      // mousedown ต้องหยุดไว้ ไม่งั้น ProseMirror ย้าย selection ออกจากรูปก่อนที่ปุ่มจะทำงาน
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
      className={cx(
        'grid h-7 min-w-7 place-items-center rounded-md px-1.5 text-[11px] font-medium transition-colors',
        active ? 'bg-white/20 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white',
      )}
    >
      {children}
    </button>
  );
}

function ImageView({ node, updateAttributes, selected, editor, deleteNode, getPos }: NodeViewProps) {
  const align = figureAlign(node.attrs.align);
  const width = figureWidth(node.attrs.width);
  const caption = typeof node.attrs.caption === 'string' ? node.attrs.caption : '';
  const src = typeof node.attrs.src === 'string' ? node.attrs.src : '';
  const alt = typeof node.attrs.alt === 'string' ? node.attrs.alt : '';
  const editable = editor.isEditable;
  const active = selected && editable;

  const split = isSplitAlign(align);

  const figRef = useRef<HTMLElement | null>(null);
  const mediaRef = useRef<HTMLDivElement | null>(null);
  // ระหว่างลากเก็บค่าไว้ใน state อย่างเดียว แล้วค่อยเขียนลง document ตอนปล่อยเมาส์
  // เพื่อให้ "ย่อขยายหนึ่งครั้ง = undo หนึ่งครั้ง" แทนที่จะเป็นร้อยครั้งตามจำนวน mousemove
  const [dragW, setDragW] = useState<number | null>(null);
  const dragRef = useRef<number | null>(null);
  const shownWidth = dragW ?? width ?? (split ? FIGURE_WRAP_DEFAULT_WIDTH : 100);

  /**
   * ProseMirror เลื่อนการเลือก node ที่ลากได้ไปทำตอน mouseup เอง
   * ซึ่งบางจังหวะไม่เกิดขึ้นเลย — สั่งเลือกเองตอนคลิกจึงแน่นอนกว่า
   * ไม่งั้นผู้ใช้คลิกรูปแล้วแถบเครื่องมือไม่ขึ้น
   */
  const selectSelf = useCallback(() => {
    if (!editable) return;
    const pos = getPos();
    if (typeof pos === 'number') editor.commands.setNodeSelection(pos);
  }, [editable, editor, getPos]);

  /**
   * โหมดนิตยสารต้องให้รูปกับข้อความอยู่ในกล่องเดียวกัน (ดูเหตุผลใน @blogaris/shared)
   * การสลับโหมดจึงไม่ใช่แค่เปลี่ยน attribute แต่ต้องย้ายย่อหน้าถัดไปเข้ามาเป็นลูกของ node นี้
   * และย้ายกลับออกไปเมื่อเลิกใช้ ทำในทรานแซกชันเดียวเพื่อให้ undo ครั้งเดียวกลับได้หมด
   */
  const setAlign = useCallback((mode: FigureAlign) => {
    const wasSplit = isSplitAlign(align);
    const willSplit = isSplitAlign(mode);

    if (wasSplit === willSplit) {
      const patch: Record<string, unknown> = { align: mode };
      // รูปเต็มความกว้างจะไม่เหลือที่ให้ข้อความไหลไปอยู่ข้าง ๆ — ย่อให้อัตโนมัติ
      if ((mode === 'wrap-left' || mode === 'wrap-right') && width === null) {
        patch.width = FIGURE_WRAP_DEFAULT_WIDTH;
      }
      updateAttributes(patch);
      return;
    }

    editor.commands.command(({ tr, dispatch }) => {
      const pos = getPos();
      if (typeof pos !== 'number') return false;
      const self = tr.doc.nodeAt(pos);
      if (!self) return false;
      const attrs: Record<string, unknown> = { ...self.attrs, align: mode };

      if (willSplit) {
        if (attrs.width === null) attrs.width = FIGURE_WRAP_DEFAULT_WIDTH;
        const after = pos + self.nodeSize;
        const next = after < tr.doc.content.size ? tr.doc.nodeAt(after) : null;
        // ดึงย่อหน้าถัดไปเข้ามา ถ้าไม่มีก็สร้างย่อหน้าว่างไว้ให้พิมพ์
        const takes = next?.type.name === 'paragraph';
        const body = takes && next ? Fragment.from(next) : Fragment.from(self.type.schema.nodes.paragraph!.create());
        if (!dispatch) return true;
        tr.replaceWith(pos, takes && next ? after + next.nodeSize : after, self.type.create(attrs, body));
        // วางเคอร์เซอร์ไว้ในข้อความ ผู้ใช้จะได้พิมพ์ต่อได้ทันทีถ้าเพิ่งสร้างย่อหน้าว่าง
        dispatch(tr);
        return true;
      }

      // ออกจากโหมดนิตยสาร — คายย่อหน้าที่อุ้มไว้กลับออกไปเป็นพี่น้อง ไม่ให้ข้อความหาย
      if (!dispatch) return true;
      const kept: ReturnType<typeof self.child>[] = [];
      self.content.forEach((child) => { if (child.content.size > 0) kept.push(child); });
      tr.replaceWith(pos, pos + self.nodeSize, [self.type.create(attrs, null), ...kept]);
      dispatch(tr);
      return true;
    });
  }, [align, editor, getPos, updateAttributes, width]);

  const startResize = useCallback((side: 'left' | 'right') => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const fig = figRef.current;
    const media = mediaRef.current;
    // โหมดนิตยสาร figure กินเต็มความกว้างอยู่แล้ว ความกว้างของรูปจึงวัดเทียบกับ figure เอง
    // โหมดอื่น figure คือตัวรูป จึงต้องวัดเทียบกับกล่องแม่
    const host = split ? fig : fig?.parentElement;
    if (!fig || !media || !host) return;

    const hostWidth = host.clientWidth;
    const startX = e.clientX;
    const startPx = media.getBoundingClientRect().width;
    const dir = side === 'right' ? 1 : -1;
    // รูปที่จัดกึ่งกลางขยายออกทั้งสองด้านพร้อมกัน ถ้าไม่คูณสองขอบรูปจะวิ่งช้ากว่าเมาส์ครึ่งหนึ่ง
    const gain = align === 'center' || split ? 2 : 1;

    const move = (ev: PointerEvent) => {
      const px = startPx + (ev.clientX - startX) * dir * gain;
      // โหมดนิตยสารเกิน 70% แล้วคอลัมน์ข้างรูปจะแคบจนอ่านไม่ออก
      const pct = Math.min(split ? 70 : 100, Math.max(10, Math.round((px / hostWidth) * 100)));
      dragRef.current = pct;
      setDragW(pct);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      const final = dragRef.current;
      dragRef.current = null;
      setDragW(null);
      if (final !== null) updateAttributes({ width: final >= 100 ? null : final });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, [align, split, updateAttributes]);

  // ถ้า component ถูกถอดออกกลางคัน (เช่นกด undo ระหว่างลาก) ต้องไม่ทิ้ง listener ค้างไว้
  useEffect(() => () => { dragRef.current = null; }, []);

  const editAlt = useCallback(() => {
    const next = window.prompt('คำอธิบายภาพสำหรับผู้ที่ใช้โปรแกรมอ่านหน้าจอ (alt text)', alt);
    if (next !== null) updateAttributes({ alt: next });
  }, [alt, updateAttributes]);

  const showCaption = active || caption.length > 0;

  return (
    <NodeViewWrapper
      as="figure"
      ref={figRef as never}
      className={cx(figureClass(align), 'relative', active && 'ba-fig--active')}
      style={
        split
          // โหมดนิตยสาร figure กินเต็มความกว้าง ความกว้างของรูปส่งผ่าน custom property
          // เพราะทั้งคอลัมน์ grid และร่องกลางของข้อความต้องอ่านค่าเดียวกันถึงจะตรงขอบ
          ? ({ '--fig-w': `${shownWidth}%` } as React.CSSProperties)
          : { width: shownWidth >= 100 ? undefined : `${shownWidth}%` }
      }
      data-align={align}
    >
      <div
        ref={mediaRef}
        className="ba-fig__media relative"
        onClick={selectSelf}
        onDragStart={() => {
          const p = getPos();
          draggingFigureFrom = typeof p === 'number' ? p : null;
        }}
      >
        <img
          src={src}
          alt={alt}
          data-drag-handle
          className={cx(
            'block h-auto w-full rounded-[10px] transition-shadow',
            active && 'ring-2 ring-[var(--brand)] ring-offset-2 ring-offset-[var(--surface)]',
            editable && 'cursor-grab',
          )}
        />

        {active && (
          <>
            {/* ที่จับย่อขยายซ้าย/ขวา */}
            {(['left', 'right'] as const).map((side) => (
              <span
                key={side}
                onPointerDown={startResize(side)}
                data-figure-ui=""
                role="separator"
                aria-label={side === 'left' ? 'ย่อขยายจากขอบซ้าย' : 'ย่อขยายจากขอบขวา'}
                className="absolute top-1/2 z-10 h-10 w-2.5 -translate-y-1/2 cursor-ew-resize rounded-full border-2 border-white bg-[var(--brand)] shadow-md"
                style={{ [side]: '-5px' } as React.CSSProperties}
              />
            ))}

            {/* ป้ายบอกความกว้างระหว่างลาก */}
            {dragW !== null && (
              <span className="pointer-events-none absolute bottom-2 right-2 rounded-md bg-black/70 px-2 py-1 text-[11px] font-medium tabular-nums text-white">
                {dragW}%
              </span>
            )}

            {/* แถบเครื่องมือ — ลอยเหนือรูป ไม่เบียดเนื้อหาให้ขยับ */}
            <div
              contentEditable={false}
              data-figure-ui=""
              className="absolute bottom-full left-1/2 z-20 mb-2 flex -translate-x-1/2 items-center gap-0.5 whitespace-nowrap rounded-lg bg-[#1c1f24] px-1.5 py-1 shadow-lg"
            >
              {LAYOUTS.map(({ mode, label }) => (
                <BarButton key={mode} title={label} active={align === mode} onClick={() => setAlign(mode)}>
                  <LayoutIcon mode={mode} />
                </BarButton>
              ))}
              <span className="mx-1 h-4 w-px bg-white/20" aria-hidden="true" />
              {FIGURE_WIDTH_PRESETS.filter((pct) => !split || pct < 100).map((pct) => (
                <BarButton
                  key={pct}
                  title={`กว้าง ${pct}%`}
                  active={(width ?? (split ? FIGURE_WRAP_DEFAULT_WIDTH : 100)) === pct}
                  onClick={() => updateAttributes({ width: pct >= 100 ? null : pct })}
                >
                  {pct}
                </BarButton>
              ))}
              <span className="mx-1 h-4 w-px bg-white/20" aria-hidden="true" />
              <BarButton title="แก้คำอธิบายภาพ (alt text)" onClick={editAlt}><IconPencil size={13} /></BarButton>
              <BarButton title="ลบรูป" onClick={() => deleteNode()}><IconTrash size={13} /></BarButton>
            </div>
          </>
        )}

      {showCaption && (
        editable ? (
          <input
            value={caption}
            data-figure-ui=""
            placeholder="ใส่คำบรรยายใต้ภาพ (ไม่บังคับ)"
            onChange={(e) => updateAttributes({ caption: e.target.value })}
            // Backspace/Enter ต้องไม่หลุดขึ้นไปถึง ProseMirror ไม่งั้นจะไปลบรูปทิ้ง
            // แต่คีย์ลัดที่กดพร้อม Cmd/Ctrl (เช่น Cmd+S บันทึก) ต้องผ่านได้ตามปกติ
            onKeyDown={(e) => { if (!e.metaKey && !e.ctrlKey) e.stopPropagation(); }}
            onMouseDown={(e) => e.stopPropagation()}
            className="mt-2 w-full border-0 bg-transparent text-center text-sm text-[var(--ink-2)] outline-none placeholder:text-[var(--ink-3)]"
          />
        ) : <figcaption>{caption}</figcaption>
      )}
      </div>

      {/*
        ต้อง render NodeViewContent เสมอแม้ไม่ได้อยู่โหมดนิตยสาร
        ไม่งั้น ProseMirror หา DOM ที่จะวางลูกไม่เจอแล้วโยน error
        โหมดอื่นไม่มีลูกอยู่แล้ว ซ่อนไว้จึงไม่กระทบอะไร
      */}
      <NodeViewContent className={cx('ba-fig__flow', !split && 'hidden')} />
    </NodeViewWrapper>
  );
}

/** อ่านค่าจาก HTML ได้ทั้งกรณีที่เป็น <figure> ของเราเอง และ <img> เปล่า ๆ ที่วางมาจากที่อื่น */
function imgOf(el: HTMLElement): HTMLImageElement | null {
  return el.tagName === 'IMG' ? (el as HTMLImageElement) : el.querySelector('img');
}

export const ResizableImage = Image.extend({
  draggable: true,
  /**
   * ปกติรูปเป็น node ใบไม้ ไม่มีลูก — ยกเว้นโหมดนิตยสารที่ต้องอุ้มข้อความไว้ข้างใน
   *
   * รับได้เฉพาะย่อหน้า ไม่ใช้ 'block*' เพราะรูปเองก็อยู่ในกลุ่ม block
   * ถ้าเปิดกว้างจะลากรูปเข้าไปวางในช่องข้อความของอีกรูปได้ แล้วสั่งโหมดนิตยสารซ้อนกันไปเรื่อย ๆ
   * จนคอลัมน์แคบจนอ่านไม่ออกและแถบเครื่องมือทับกันเป็นตั้ง
   *
   * เลือกย่อหน้าอย่างเดียวเพราะปิดทางได้สนิทจริง — list กับ blockquote รับ block ต่อได้อีกชั้น
   * ช่องโหว่จะย้ายไปอยู่ที่นั่นแทน ส่วนย่อหน้ารับได้แต่ข้อความล้วน จึงซ้อนรูปไม่ได้เลย
   * '*' (ไม่ใช่ '+') เพราะรูปที่ไม่ได้อยู่โหมดนิตยสารต้องไม่มีลูกได้
   */
  content: 'paragraph*',
  isolating: true,

  addAttributes() {
    return {
      ...this.parent?.(),
      src: { default: null, parseHTML: (el) => imgOf(el)?.getAttribute('src') ?? null },
      alt: { default: null, parseHTML: (el) => imgOf(el)?.getAttribute('alt') ?? null },
      title: { default: null, parseHTML: (el) => imgOf(el)?.getAttribute('title') ?? null },
      width: {
        default: null,
        parseHTML: (el) => figureWidth(/^([\d.]+)%/.exec(el.style?.width ?? '')?.[1]),
        renderHTML: () => ({}),
      },
      align: {
        default: 'center',
        parseHTML: (el) => figureAlign(el.getAttribute?.('data-align')),
        renderHTML: () => ({}),
      },
      caption: {
        default: null,
        parseHTML: (el) => el.querySelector?.('figcaption')?.textContent?.trim() || null,
        renderHTML: () => ({}),
      },
      // ขนาดจริงของไฟล์ — ใส่ลง <img width height> ตอน render เพื่อให้เบราว์เซอร์
      // จองพื้นที่ไว้ก่อนรูปโหลดเสร็จ (ข้อ 8.3 CLS = 0)
      naturalWidth: { default: null, parseHTML: (el) => Number(imgOf(el)?.getAttribute('width')) || null, renderHTML: () => ({}) },
      naturalHeight: { default: null, parseHTML: (el) => Number(imgOf(el)?.getAttribute('height')) || null, renderHTML: () => ({}) },
    };
  },

  parseHTML() {
    return [
      // ข้อความในโหมดนิตยสารอยู่ใน .ba-fig__flow — ชี้ contentElement ไปที่นั่น
      // ไม่งั้น <figcaption> จะถูกอ่านกลับเข้ามาเป็นย่อหน้าซ้ำ
      {
        tag: 'figure.ba-fig',
        contentElement: (el: HTMLElement) => el.querySelector('.ba-fig__flow') ?? document.createElement('div'),
      },
      { tag: 'img[src]' },
    ];
  },

  renderHTML({ node }) {
    const align = figureAlign(node.attrs.align);
    const split = isSplitAlign(align);
    const width = figureWidth(node.attrs.width);
    const img: Record<string, string> = { src: String(node.attrs.src ?? ''), alt: String(node.attrs.alt ?? '') };
    if (node.attrs.title) img.title = String(node.attrs.title);
    if (node.attrs.naturalWidth) img.width = String(node.attrs.naturalWidth);
    if (node.attrs.naturalHeight) img.height = String(node.attrs.naturalHeight);
    const fig: Record<string, string> = { class: figureClass(align), 'data-align': align };
    if (width !== null) fig.style = split ? `--fig-w:${width}%` : `width:${width}%`;
    const caption = typeof node.attrs.caption === 'string' ? node.attrs.caption.trim() : '';
    const media: unknown[] = ['div', { class: 'ba-fig__media' }, ['img', img]];
    if (caption) media.push(['figcaption', {}, caption]);
    return split
      ? ['figure', fig, media, ['div', { class: 'ba-fig__flow' }, 0]]
      : ['figure', fig, media];
  },

  addProseMirrorPlugins() {
    const type = this.type;
    return [
      new Plugin({
        key: new PluginKey('blogarisFigureDrag'),
        props: {
          handleDOMEvents: {
            /**
             * ล้างตำแหน่งต้นทางเมื่อเริ่มลาก "สิ่งอื่นที่ไม่ใช่รูป"
             * กันไม่ให้การลากข้อความไปหยิบตำแหน่งรูปจากการลากครั้งก่อนมาใช้แล้วย้ายผิดตัว
             *
             * ห้ามล้างแบบไม่มีเงื่อนไข: TipTap สร้าง React root แยกไว้ข้างใน .tiptap
             * onDragStart ของ node view จึงทำงาน "ก่อน" ปลั๊กอินตัวนี้
             * ถ้าล้างทิ้งเฉย ๆ จะไปลบค่าที่ node view เพิ่งเซ็ตมา แล้วตอนวางก็ไม่รู้ว่าต้องย้ายอะไร
             */
            dragstart(view, event) {
              const t = event.target as HTMLElement | null;
              const fromFigure = Boolean(t?.closest?.('.ba-fig__media'));
              if (!fromFigure) draggingFigureFrom = null;
              return false;
            },

            /**
             * ต้นตอที่แท้จริงของอาการ "ลากแล้วกลายเป็นคัดลอก"
             *
             * ProseMirror ตั้งเวลาไว้ล้าง view.dragging (ข้อมูลว่ากำลังลาก node ไหน)
             * หลัง dragend เพียง 50 มิลลิวินาที แต่บนเครื่องจริงเบราว์เซอร์ยิง dragend
             * มา "ก่อน" drop ได้ พอถึงตอนวางข้อมูลต้นทางจึงหายไปแล้ว
             * ProseMirror เลยคิดว่าเป็นการวางของจากที่อื่น = แปะใหม่โดยไม่ลบของเดิม
             *
             * ตัวล้างนี้ไม่จำเป็นเลย เพราะ editHandlers.drop ล้าง view.dragging
             * ให้อยู่แล้วใน finally เรากินอีเวนต์ไว้ (return true) ไม่ให้ตัวเดิมทำงาน
             * แล้วล้างเองด้วยเวลาที่ยาวพอ พร้อมเช็คว่ายังเป็นการลากครั้งเดิมอยู่จริง
             * เหมือนที่ ProseMirror ทำ จะได้ไม่ไปล้างการลากครั้งใหม่ทิ้ง
             *
             * แก้ตรงนี้ครอบคลุมการลากทุกชนิด ไม่ใช่แค่รูป — ลากข้อความก็เคยโดนแบบเดียวกัน
             */
            dragend(view) {
              const current = view.dragging;
              window.setTimeout(() => {
                if (view.dragging === current) view.dragging = null;
              }, 2000);
              return true;
            },
          },

          handleDrop(view, event, _slice, _moved) {
            const from = draggingFigureFrom;
            draggingFigureFrom = null;
            if (from === null) return false;
            // กด Option ค้าง = ตั้งใจคัดลอก ปล่อยให้เป็นพฤติกรรมมาตรฐาน
            if ((event as DragEvent).altKey) return false;

            const { state } = view;
            const node = state.doc.nodeAt(from);
            if (!node || node.type !== type) return false;

            const mouse = view.posAtCoords({ left: (event as DragEvent).clientX, top: (event as DragEvent).clientY });
            if (!mouse) return false;

            const slice = new Slice(Fragment.from(node), 0, 0);
            const target = dropPoint(state.doc, mouse.pos, slice) ?? mouse.pos;
            // วางทับที่เดิม — ไม่ต้องทำอะไร แต่ต้องกินอีเวนต์ไว้ ไม่งั้น ProseMirror จะแปะซ้ำ
            if (target >= from && target <= from + node.nodeSize) {
              event.preventDefault();
              return true;
            }

            const tr = state.tr.delete(from, from + node.nodeSize);
            const at = tr.mapping.map(target);
            tr.insert(at, node);
            const $at = tr.doc.resolve(at);
            if ($at.nodeAfter?.eq(node)) tr.setSelection(NodeSelection.create(tr.doc, at));
            view.dispatch(tr.scrollIntoView());
            event.preventDefault();
            return true;
          },
        },
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageView, {
      /**
       * stopEvent ที่ TipTap ให้มาโดยปริยายจะยอมส่ง dragstart ต่อให้ ProseMirror
       * ก็ต่อเมื่อมันดัก mousedown บน [data-drag-handle] ได้ก่อนเท่านั้น
       * พอ node นี้มีลูกได้ (โหมดนิตยสาร) กล่องครอบเลยไม่ใช่ contentEditable=false อีกต่อไป
       * mousedown จึงถูกตีความเป็น "อีเวนต์ของช่องกรอก" แล้วถูกกินไปก่อน
       * ผลคือ ProseMirror ไม่เคยรู้ว่ากำลังลาก node ไหน ตอนวางจึงแปะรูปใหม่โดยไม่ลบต้นทาง
       * = ลากแล้วกลายเป็นคัดลอกแทนที่จะย้าย
       *
       * จึงตัดสินใจเองให้ชัด: อะไรที่เป็นตัวควบคุมของเรา (ปุ่ม/ที่จับ/ช่องคำบรรยาย) กินไว้เอง
       * นอกนั้นส่งให้ ProseMirror ทั้งหมด รวมถึง drag/drop
       */
      stopEvent: ({ event }) => {
        // อีเวนต์ลาก/วางต้องถึง ProseMirror เสมอ
        //
        // แถบเครื่องมือของรูปที่ถูกเลือกลอยค้างอยู่เหนือรูป และช่องคำบรรยายกินความกว้างใต้รูป
        // ถ้าปล่อยเมาส์โดนสองอันนี้แล้วเรากินอีเวนต์ drop ไว้ ProseMirror จะไม่เห็นการวางเลย
        // เบราว์เซอร์จึงวางเองแบบ contenteditable ซึ่งแปะสำเนาลงไปโดยไม่ลบต้นทาง
        if (event.type === 'drop' || event.type.startsWith('drag')) return false;
        const target = event.target as HTMLElement | null;
        return Boolean(target?.closest?.('[data-figure-ui]'));
      },
    });
  },
});
